import React, {
  useCallback,
  useMemo,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import Axios, { AxiosRequestConfig } from "axios";
import { produce } from "immer";
import { useImmer } from "use-immer";
import Cookies from "js-cookie";
import {
  useAsyncState,
  isCapacitor as isCapacitorFunc,
  wrap,
} from "@hilma/tools";
import {
  AccessTokenCookieContext,
  AuthContext as AC,
  AuthFetchContext,
  createCookieString,
  GetAccessTokenContext,
  GetAuthItemContext,
  HasTwoFactorTokenContext,
  InterceptorsContext as IC,
  IsAuthenticatedContext,
  KlsContext,
  LoginContext,
  LoginWithCodeContext,
  LogoutContext,
  MultiSetAuthItemContext as MSAIC,
  RemoveAuthItemContext,
  SendCodeContext,
  SetAuthItemContext as SAIC,
  Storage,
  SuperAuthFetchContext,
} from "@hilma/auth-core";

import { Auth, AuthProviderProps } from "../types";

export const AuthContext = AC as React.Context<Auth | null>;
export const SetAuthItemContext = SAIC as React.Context<
  Auth["setAuthItem"] | null
>;
export const MultiSetAuthItemContext = MSAIC as React.Context<
  Auth["multiSetAuthItem"] | null
>;
export const InterceptorsContext = IC as React.Context<
  Auth["interceptors"] | null
>;

const AuthProvider = forwardRef(
  (
    {
      logoutOnUnauthorized = false,
      accessTokenCookie: at = "access_token",
      basename = "",
      inactivityTime = false,
      twoFactorCookie: tfc = "two_factor_token",
      children,
    }: AuthProviderProps,
    ref?: React.Ref<Auth>,
  ) => {
    const [interceptors, updateInterceptors] = useImmer<Auth["interceptors"]>({
      proxyAndHeadersId: null,
      capacitorHeadersId: null,
      logoutId: null,
    });

    const accessTokenCookie = at as "access_token";
    const twoFactorCookie = tfc as "two_factor_token";

    const storageKeys = useRef([
      "kl",
      "klo",
      "kloo",
      "klk",
      accessTokenCookie,
      twoFactorCookie,
    ]).current;

    const isCapacitor = useMemo(isCapacitorFunc, []);

    const [storage, setStorage, getStorage] = useAsyncState<Storage>(() => {
      const storage = storageKeys.reduce((storage, key) => {
        return {
          ...storage,
          [key]: isCapacitor ? localStorage.getItem(key) : Cookies.get(key),
        };
      }, {} as Storage);
      return storage;
    });

    const proxy = useRef(process.env.REACT_APP_DOMAIN || "" + basename).current;

    const kls = useMemo(
      () => ({ kl: storage.kl, klo: storage.klo }),
      [storage],
    );

    const getAuthItem = useCallback(
      (key: keyof Storage) => storage[key],
      [storage],
    );

    const setAuthItem = useCallback(
      (
        key: keyof Storage,
        value: string,
        setLocalStorage: boolean | undefined = isCapacitor,
        setCookies: boolean | undefined = !isCapacitor,
        changeStorage: boolean | undefined = true,
      ) => {
        if (setCookies) Cookies.set(key, value);
        if (setLocalStorage) localStorage.setItem(key, value);
        if (!changeStorage) return getStorage();
        return setStorage(
          produce((storage: Storage) => {
            storage[key] = value;
          }),
        );
      },
      [isCapacitor],
    );

    const multiSetAuthItem = useCallback(
      async (
        keyValuePairs: [keyof Storage, string][],
        setLocalStorage: boolean | undefined = isCapacitor,
        setCookies: boolean | undefined = !isCapacitor,
        changeStorage: boolean | undefined = true,
      ) => {
        await Promise.all(
          keyValuePairs.map(async ([key, value]) =>
            setAuthItem(
              key as keyof Storage,
              value,
              setLocalStorage,
              setCookies,
              false,
            ),
          ),
        );

        if (!changeStorage) return getStorage();

        const newStorage: Record<string, string> = {};
        keyValuePairs.forEach(([key, value]) => {
          newStorage[key] = value;
        });

        return setStorage((storage) => ({
          ...storage,
          ...newStorage,
        }));
      },
      [setAuthItem, getStorage],
    );

    const removeAuthItem = useCallback((key: keyof Storage) => {
      localStorage.removeItem(key);
      Cookies.remove(key);
      return setStorage(
        produce((storage: Storage) => {
          storage[key] = null;
        }),
      );
    }, []);

    const getAccessToken = useCallback(
      () => getAuthItem(accessTokenCookie),
      [getAuthItem],
    );

    const isAuthenticated = useMemo(() => {
      const at = getAccessToken();
      return at !== null && at !== undefined && at !== "";
    }, [getAccessToken]);

    const hasTwoFactorToken = useMemo(() => {
      const tft = getAuthItem(twoFactorCookie);
      return tft !== null && tft !== undefined && tft !== "";
    }, [getAuthItem, twoFactorCookie]);

    const logout = useCallback((deleteTwoFactorToken: boolean = false) => {
      storageKeys.forEach(
        (key) =>
          ((deleteTwoFactorToken === true && key === twoFactorCookie) ||
            key !== twoFactorCookie) &&
          (isCapacitor ? localStorage.removeItem(key) : Cookies.remove(key)),
      );
      return setStorage((storage) => ({
        klo: null,
        kl: null,
        kloo: null,
        klk: null,
        [accessTokenCookie]: null,
        [twoFactorCookie]: deleteTwoFactorToken
          ? null
          : storage[twoFactorCookie],
      }));
    }, []);

    const authFetch = useCallback(
      async (
        input: RequestInfo,
        init: RequestInit = {},
        fetchOffline = false,
      ) => {
        try {
          const storage = await getStorage();
          if (!navigator.onLine && !fetchOffline) throw "NO_INTERNET";
          const basicHeaders: RequestInit["headers"] = !(
            init.body instanceof FormData
          )
            ? {
                "Content-Type": "application/json",
                Accept: "application/json",
              }
            : {};
          if (init.headers) init.headers = { ...basicHeaders, ...init.headers };
          else init.headers = basicHeaders;
          if (isCapacitor)
            init.headers = {
              ...init.headers,
              Authorization: storage[accessTokenCookie]!,
              Cookie: createCookieString(
                storage as unknown as Record<string, string | null>,
              ),
            };
          const res = await fetch(proxy + input, init);
          const data = await res.json();
          if (!res.ok) throw data;
          return data;
        } catch (error) {
          if (
            ((error as { statusCode?: number })?.statusCode === 401 ||
              (error as { error?: { statusCode?: number } })?.error
                ?.statusCode === 401) &&
            logoutOnUnauthorized
          )
            logout();
          throw error;
        }
      },
      [logout, proxy, isCapacitor],
    );

    const superAuthFetch = useCallback(
      async <D, E>(
        input: RequestInfo,
        init: RequestInit = {},
        fetchOffline = false,
      ): Promise<[D, E]> => {
        try {
          const result = await authFetch(input, init, fetchOffline);
          return [result, null as E];
        } catch (error) {
          return [null as D, error as E];
        }
      },
      [authFetch],
    );

    const login = useCallback(
      async <U = any, M = any>(
        input: string,
        body: Partial<Record<"password" | "username" | "email", string>>,
        deleteTwoFactorToken: boolean = false,
      ): Promise<{
        success: boolean;
        user?: U | undefined;
        msg?: M | undefined;
      }> => {
        try {
          const { data } = await Axios.post(input, body);

          const {
            klo,
            kl,
            kloo,
            klk,
            [accessTokenCookie]: access_token,
            id,
          } = data;

          if (isCapacitor)
            (
              [
                ["klo", klo || ""],
                ["kl", kl || ""],
                ["kloo", kloo || ""],
                ["klk", klk || ""],
                [accessTokenCookie, (access_token ?? id) || ""],
              ] as Array<[keyof Storage, string]>
            ).forEach(([key, value]) =>
              setAuthItem(key, value, isCapacitor, false, false),
            );

          if (deleteTwoFactorToken) removeAuthItem(twoFactorCookie);

          await setStorage((storage) => ({
            klo,
            kl,
            klk,
            kloo,
            [accessTokenCookie]: access_token ?? id,
            [twoFactorCookie]: deleteTwoFactorToken
              ? null
              : storage[twoFactorCookie],
          }));

          return { success: true, user: data };
        } catch (error) {
          return { success: false, msg: error as M };
        }
      },
      [setAuthItem, isCapacitor, removeAuthItem],
    );

    const sendCode = useCallback(
      async <D = any, M = any>(
        input: string,
        body: Partial<Record<"password" | "username" | "email", string>>,
      ): Promise<{
        success: boolean;
        data?: D | undefined;
        msg?: M | undefined;
      }> => {
        try {
          const { data } = await Axios.post(input, body);
          const { [twoFactorCookie]: two_factor_token } = data;

          await setAuthItem(twoFactorCookie, two_factor_token, false, false);
          return { success: true, data };
        } catch (error) {
          return { success: false, msg: error as M };
        }
      },
      [setAuthItem],
    );

    const loginWithCode = useCallback(
      async (input: string, code: string) => {
        const storage = await getStorage();
        const data = await login(
          input,
          { code, [twoFactorCookie]: storage[twoFactorCookie] } as Partial<
            Record<"password" | "username" | "email", string>
          >,
          true,
        );
        return data;
      },
      [login, getStorage],
    );

    useEffect(() => {
      const proxyAndHeadersId = Axios.interceptors.request.use(
        (config: AxiosRequestConfig) => {
          const basicHeaders: RequestInit["headers"] = !(
            config.data instanceof FormData
          )
            ? {
                "Content-Type": "application/json",
                Accept: "application/json",
              }
            : {};
          if (config.headers)
            config.headers = { ...basicHeaders, ...config.headers };
          else config.headers = basicHeaders;
          if (
            config.url?.indexOf("http://") !== 0 &&
            config.url?.indexOf("https://") !== 0
          )
            config.baseURL = proxy;

          return config;
        },
      );

      updateInterceptors((draft) => {
        draft.proxyAndHeadersId = proxyAndHeadersId;
      });

      return () => {
        Axios.interceptors.request.eject(proxyAndHeadersId);
      };
    }, [proxy]);

    useEffect(() => {
      if (!isCapacitor) return;

      const capacitorHeadersId = Axios.interceptors.request.use(
        async (config: AxiosRequestConfig) => {
          const storage = await getStorage();
          const basicHeaders: RequestInit["headers"] = {
            Authorization: storage[accessTokenCookie]!,
            Cookie: createCookieString(
              storage as unknown as Record<string, string | null>,
            ),
          };
          if (config.headers)
            config.headers = { ...basicHeaders, ...config.headers };
          else config.headers = basicHeaders;

          return config;
        },
      );

      updateInterceptors((draft) => {
        draft.capacitorHeadersId = capacitorHeadersId;
      });

      return () => {
        Axios.interceptors.request.eject(capacitorHeadersId);
      };
    }, [isCapacitor]);

    useEffect(() => {
      const logoutId = Axios.interceptors.response.use(
        (response) => response,
        (error) => {
          if (error?.response?.status === 401 && logoutOnUnauthorized) logout();
          return Promise.reject(error.response);
        },
      );

      updateInterceptors((draft) => {
        draft.logoutId = logoutId;
      });

      return () => {
        Axios.interceptors.response.eject(logoutId);
      };
    }, [logout, logoutOnUnauthorized]);

    useEffect(() => {
      if (!inactivityTime) return;

      let timeOunt: NodeJS.Timeout;

      const resetTimer = () => {
        clearTimeout(timeOunt);
        timeOunt = setTimeout(logout, 10 * 60 * 1000);
      };

      resetTimer();

      const events = [
        "load",
        "mousemove",
        "mousedown",
        "touchstart",
        "click",
        "scroll",
        "mousewheel",
        "DOMMouseScroll",
        "keypress",
      ];

      events.forEach((event) => document.addEventListener(event, resetTimer));

      return () => {
        events.forEach((event) =>
          document.removeEventListener(event, resetTimer),
        );
      };
    }, [inactivityTime]);

    const value = useMemo<Auth>(
      () => ({
        kls,
        setAuthItem,
        multiSetAuthItem,
        getAuthItem,
        removeAuthItem,
        getAccessToken,
        isAuthenticated,
        hasTwoFactorToken,
        logout,
        authFetch,
        superAuthFetch,
        login,
        sendCode,
        loginWithCode,
        interceptors,
        accessTokenCookie,
      }),
      [
        kls,
        setAuthItem,
        multiSetAuthItem,
        getAuthItem,
        removeAuthItem,
        getAccessToken,
        isAuthenticated,
        hasTwoFactorToken,
        logout,
        authFetch,
        superAuthFetch,
        login,
        sendCode,
        loginWithCode,
        interceptors,
        accessTokenCookie,
      ],
    );

    useImperativeHandle(ref, () => value, [value]);

    return wrap(
      [AuthContext.Provider, { value }],
      [KlsContext.Provider, { value: kls }],
      [SetAuthItemContext.Provider, { value: setAuthItem }],
      [MultiSetAuthItemContext.Provider, { value: multiSetAuthItem }],
      [
        GetAuthItemContext.Provider,
        { value: getAuthItem as Auth["getAuthItem"] },
      ],
      [
        RemoveAuthItemContext.Provider,
        { value: removeAuthItem as Auth["removeAuthItem"] },
      ],
      [GetAccessTokenContext.Provider, { value: getAccessToken }],
      [IsAuthenticatedContext.Provider, { value: isAuthenticated }],
      [HasTwoFactorTokenContext.Provider, { value: hasTwoFactorToken }],
      [LogoutContext.Provider, { value: logout }],
      [AuthFetchContext.Provider, { value: authFetch }],
      [SuperAuthFetchContext.Provider, { value: superAuthFetch }],
      [LoginContext.Provider, { value: login }],
      [SendCodeContext.Provider, { value: sendCode }],
      [LoginWithCodeContext.Provider, { value: loginWithCode }],
      [InterceptorsContext.Provider, { value: interceptors }],
      [AccessTokenCookieContext.Provider, { value: accessTokenCookie }],
    )(<>{children}</>);
  },
);

export default AuthProvider;
